/**
 * Turn an apply-changes validation 400 into a message that names the row it came
 * from (VALE-1576).
 *
 * The reported failure: a reviewer met `nested property institution must be
 * either object or array` in a red banner above a twenty-row review, with no
 * indication of which row carried it and no way to find or clear it. The only
 * offered actions were "Keep editing" and "Save changes", so the extraction could
 * not be saved and the only exit was discarding three hours of corrections.
 *
 * The backend now returns the payload path it had always computed
 * (`validation-exception.factory.ts`), and the submitted request is right here,
 * so the row can be named from the two together without another round trip.
 */

/** One failed constraint and the payload path that carries it. */
export interface ValidationErrorDetail {
  path: string;
  message: string;
}

/** The slice of the apply request this needs — deliberately structural. */
interface ApplyRequestLike {
  contacts?: unknown[];
  inventoryItems?: unknown[];
}

/** How a path's leading segment reads to a reviewer. */
const SECTION_LABELS: Record<string, string> = {
  contacts: 'contact',
  inventoryItems: 'asset or liability',
  deceased: "the deceased's details",
  files: 'document'
};

/** Read `details` off an arbitrary error response body, tolerating its absence. */
export function readValidationDetails(
  responseBody: unknown
): ValidationErrorDetail[] {
  if (!responseBody || typeof responseBody !== 'object') return [];
  const details = (responseBody as { details?: unknown }).details;
  if (!Array.isArray(details)) return [];
  return details.filter(
    (d): d is ValidationErrorDetail =>
      !!d &&
      typeof d === 'object' &&
      typeof (d as ValidationErrorDetail).path === 'string' &&
      typeof (d as ValidationErrorDetail).message === 'string'
  );
}

/**
 * A short human label for the row at `index` of `rows`, drawn from whatever the
 * payload actually carries. Returns undefined when the row says nothing useful
 * about itself — a positional fallback still beats a bare constraint.
 */
function describeRow(
  rows: unknown[] | undefined,
  index: number
): string | undefined {
  const row = rows?.[index];
  if (!row || typeof row !== 'object') return undefined;
  const proposed = (row as { proposed?: unknown }).proposed;
  if (!proposed || typeof proposed !== 'object') return undefined;
  const p = proposed as Record<string, unknown>;
  const itemInfo = (p.itemInfo ?? {}) as Record<string, unknown>;

  const candidates = [
    p.organisationName,
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || undefined,
    itemInfo.name,
    itemInfo.accountName,
    itemInfo.description,
    p.institutionName,
    (p.institution as { name?: unknown } | undefined)?.name,
    p.type
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim())
      return candidate.trim();
  }
  return undefined;
}

/**
 * The reviewer-facing sentence for a validation failure.
 *
 * Leads with WHERE, because that is the part the reviewer could not previously
 * discover, and keeps the constraint text after it so nothing is lost for someone
 * reporting the problem. Falls back to the raw messages when the response carries
 * no usable path, which is what every non-validation 400 will do.
 */
export function describeValidationFailure(
  responseBody: unknown,
  request: ApplyRequestLike,
  fallback: string
): string {
  const details = readValidationDetails(responseBody);
  if (details.length === 0) return fallback;

  const described = details.map((detail) => {
    const [section, rawIndex] = detail.path.split('.');
    const label = SECTION_LABELS[section];
    if (!label) return detail.message;

    const index = Number(rawIndex);
    if (!Number.isInteger(index)) return `${label} (${detail.message})`;

    const rows =
      section === 'contacts' ? request.contacts : request.inventoryItems;
    const name = describeRow(rows, index);
    // Position is 1-based for a reader counting rows on screen.
    const where = name
      ? `${label} "${name}"`
      : `${label} ${index + 1} in the list`;
    // The constraint goes in brackets so the ROW is what the eye lands on, and
    // so joining several of these needs no nested colons.
    return `${where} (${detail.message})`;
  });

  const unique = Array.from(new Set(described));

  /*
    **One line, because this renders in a Banner's `title` slot.** The banner
    takes the whole string as its heading, so a multi-sentence paragraph would
    render as heading text. Naming the row is the entire job here; the reviewer
    can act once they know which one it is, and a "now go and fix it" sentence
    buys nothing at the cost of a heading that reads as broken.
  */
  return unique.length === 1
    ? `Couldn't save ${unique[0]}`
    : `Couldn't save ${unique.length} items: ${unique.join('; ')}`;
}
